import { useEffect, useMemo, useState } from "react";
import { Schedule as ScheduleAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import {
  Plus, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Pencil, Trash2, Clock, X,
} from "lucide-react";
import { toast } from "sonner";

const DAY_LABELS = ["Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota", "Niedziela"];
const DAY_SHORT = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Ndz"];

const STATUS_META = {
  pending:  { label: "Oczekuje", cls: "bg-amber-100 text-amber-800 border-amber-400" },
  approved: { label: "Zatwierdzone", cls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  rejected: { label: "Odrzucone", cls: "bg-red-100 text-red-800 border-red-400" },
  modified: { label: "Zmienione", cls: "bg-blue-100 text-blue-800 border-blue-400" },
};

/** Monday of the week containing `d` (ISO YYYY-MM-DD). */
function mondayOf(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // 0=Mon .. 6=Sun
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function fmtISO(d) {
  return d.toISOString().slice(0, 10);
}
function fmtHumanRange(monday) {
  const end = new Date(monday);
  end.setDate(end.getDate() + 6);
  const opt = { day: "numeric", month: "long" };
  return `${monday.toLocaleDateString("pl-PL", opt)} — ${end.toLocaleDateString("pl-PL", opt)} ${end.getFullYear()}`;
}

export default function SchedulePage() {
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [data, setData] = useState({ slots: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [couriers, setCouriers] = useState([]);
  const [editing, setEditing] = useState(null); // {slot | 'new'}

  const canManage = data.can_manage;

  const load = async () => {
    setLoading(true);
    try {
      const res = await ScheduleAPI.list({ week_start: fmtISO(monday) });
      setData(res);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [monday]);

  useEffect(() => {
    if (canManage) {
      ScheduleAPI.couriers().then(setCouriers).catch(() => {});
    }
  }, [canManage]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [monday]);

  // Group slots by user_id then by date
  const grouped = useMemo(() => {
    const byUser = {};
    for (const s of data.slots || []) {
      if (!byUser[s.user_id]) {
        byUser[s.user_id] = { user_id: s.user_id, name: s.name || s.username, byDate: {} };
      }
      const key = s.date;
      if (!byUser[s.user_id].byDate[key]) byUser[s.user_id].byDate[key] = [];
      byUser[s.user_id].byDate[key].push(s);
    }
    return Object.values(byUser).sort((a, b) => a.name.localeCompare(b.name));
  }, [data.slots]);

  const handleAction = async (action, slot) => {
    try {
      if (action === "approve") await ScheduleAPI.approve(slot.id);
      else if (action === "reject") await ScheduleAPI.reject(slot.id);
      else if (action === "delete") {
        if (!(await confirm("Usunąć ten slot?", { tone: "danger", confirmLabel: "Usuń" }))) return;
        await ScheduleAPI.remove(slot.id);
      }
      toast.success("Zapisano");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-label mb-1">// Grafik pracy</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Grafik</h1>
          <p className="text-zinc-600 mt-2 text-sm">
            {canManage
              ? "Zatwierdzaj lub zmieniaj sloty zgłoszone przez kurierów. Kliknij dzień, aby dodać slot dla wybranej osoby."
              : "Zgłoś swoją dostępność. Koordynator zatwierdzi lub zmieni Twój slot — dostaniesz powiadomienie."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() - 7); return d; })}
            data-testid="btn-week-prev"
            className="p-2 border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="px-4 py-2 border-2 border-zinc-900 font-mono text-sm min-w-[280px] text-center" data-testid="week-label">
            {fmtHumanRange(monday)}
          </div>
          <button
            onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() + 7); return d; })}
            data-testid="btn-week-next"
            className="p-2 border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMonday(mondayOf(new Date()))}
            data-testid="btn-week-today"
            className="px-3 py-2 border-2 border-zinc-900 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white"
          >
            Dzisiaj
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="btn-new-slot"
            className="inline-flex items-center gap-2 bg-brand text-white px-4 py-2 font-bold uppercase tracking-wider text-xs hover:bg-brand-hover"
          >
            <Plus className="w-4 h-4" /> {canManage ? "Nowy slot" : "Zgłoś dostępność"}
          </button>
        </div>
      </div>

      {loading && <div className="py-10 text-center text-zinc-500 text-sm">Ładowanie...</div>}

      {!loading && (
        <div className="overflow-x-auto border-2 border-zinc-900" data-testid="schedule-grid">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-zinc-900 text-white">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest border-r border-zinc-700 w-48">
                  Kurier
                </th>
                {days.map((d, i) => {
                  const isToday = fmtISO(d) === fmtISO(new Date());
                  return (
                    <th
                      key={i}
                      className={`text-left px-3 py-3 text-xs font-bold uppercase tracking-widest border-r border-zinc-700 last:border-r-0 ${isToday ? "bg-brand" : ""}`}
                    >
                      <div>{DAY_SHORT[i]}</div>
                      <div className="text-[10px] font-mono opacity-70">{d.getDate()}.{d.getMonth() + 1}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-sm text-zinc-500">
                    Brak slotów w tym tygodniu. Kliknij &bdquo;{canManage ? "Nowy slot" : "Zgłoś dostępność"}&rdquo;.
                  </td>
                </tr>
              )}
              {grouped.map((g) => (
                <tr key={g.user_id} className="border-t border-zinc-200" data-testid={`row-${g.user_id}`}>
                  <td className="px-4 py-3 font-bold text-sm border-r border-zinc-200 bg-zinc-50">
                    {g.name}
                  </td>
                  {days.map((d, i) => {
                    const key = fmtISO(d);
                    const slots = g.byDate[key] || [];
                    return (
                      <td key={i} className="px-2 py-2 border-r border-zinc-200 last:border-r-0 align-top min-h-[60px]">
                        <div className="space-y-1">
                          {slots.map((s) => (
                            <SlotChip
                              key={s.id}
                              slot={s}
                              canManage={canManage}
                              isOwner={user?.user_id === s.user_id}
                              onEdit={() => setEditing(s)}
                              onAction={handleAction}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SlotEditor
          slot={editing === "new" ? null : editing}
          canManage={canManage}
          couriers={couriers}
          currentUser={user}
          defaultDate={fmtISO(days[0])}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SlotChip({ slot, canManage, isOwner, onEdit, onAction }) {
  const meta = STATUS_META[slot.status] || STATUS_META.pending;
  const timeLabel = slot.all_day
    ? "Cały dzień"
    : slot.start_time && slot.end_time
    ? `${slot.start_time}–${slot.end_time}`
    : slot.start_time || "—";
  return (
    <div
      className={`border ${meta.cls} px-2 py-1.5 text-[11px] font-mono`}
      data-testid={`slot-${slot.id}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-bold flex items-center gap-1">
          <Clock className="w-3 h-3" /> {timeLabel}
        </span>
        <span className="text-[9px] uppercase opacity-80">{meta.label}</span>
      </div>
      {slot.notes && <div className="text-[10px] mt-1 italic truncate" title={slot.notes}>{slot.notes}</div>}
      {slot.review_notes && (
        <div className="text-[10px] mt-1 opacity-80" title={slot.review_notes}>
          <span className="font-bold">Uwagi:</span> {slot.review_notes}
        </div>
      )}
      <div className="flex gap-1 mt-1.5">
        {canManage && slot.status !== "approved" && (
          <button
            onClick={() => onAction("approve", slot)}
            data-testid={`slot-approve-${slot.id}`}
            title="Zatwierdź"
            className="p-1 hover:bg-emerald-600 hover:text-white border border-emerald-600 text-emerald-700"
          >
            <CheckCircle2 className="w-3 h-3" />
          </button>
        )}
        {canManage && slot.status !== "rejected" && (
          <button
            onClick={() => onAction("reject", slot)}
            data-testid={`slot-reject-${slot.id}`}
            title="Odrzuć"
            className="p-1 hover:bg-red-600 hover:text-white border border-red-600 text-red-700"
          >
            <XCircle className="w-3 h-3" />
          </button>
        )}
        {(canManage || (isOwner && slot.status === "pending")) && (
          <button
            onClick={onEdit}
            data-testid={`slot-edit-${slot.id}`}
            title="Edytuj"
            className="p-1 hover:bg-zinc-900 hover:text-white border border-zinc-400"
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {(canManage || (isOwner && slot.status === "pending")) && (
          <button
            onClick={() => onAction("delete", slot)}
            data-testid={`slot-delete-${slot.id}`}
            title="Usuń"
            className="p-1 hover:bg-red-600 hover:text-white border border-red-400 text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function SlotEditor({ slot, canManage, couriers, currentUser, defaultDate, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    user_id: slot?.user_id || currentUser?.user_id || "",
    date: slot?.date || defaultDate || "",
    all_day: slot?.all_day || false,
    start_time: slot?.start_time || "08:00",
    end_time: slot?.end_time || "16:00",
    notes: slot?.notes || "",
    review_notes: slot?.review_notes || "",
  }));
  const [busy, setBusy] = useState(false);

  const isEdit = !!slot;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        date: form.date,
        all_day: form.all_day,
        start_time: form.all_day ? "" : form.start_time,
        end_time: form.all_day ? "" : form.end_time,
        notes: form.notes,
        review_notes: canManage ? form.review_notes : undefined,
      };
      if (isEdit) {
        await ScheduleAPI.update(slot.id, payload);
        toast.success("Zaktualizowano slot");
      } else {
        if (canManage) payload.user_id = form.user_id;
        await ScheduleAPI.create(payload);
        toast.success(canManage ? "Slot dodany" : "Zgłoszenie wysłane — czeka na akceptację");
      }
      onSaved();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="slot-editor-overlay"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md border-2 border-zinc-900 shadow-[12px_12px_0_0_#18181b]"
        data-testid="slot-editor"
      >
        <div className="flex items-start justify-between px-6 py-4 border-b-2 border-zinc-900">
          <div>
            <div className="font-label">// Grafik</div>
            <h2 className="font-heading font-black text-xl uppercase tracking-tight">
              {isEdit ? "Edytuj slot" : canManage ? "Nowy slot" : "Zgłoś dostępność"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100" data-testid="slot-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {canManage && !isEdit && (
            <div>
              <label className="font-label text-[10px] block mb-1">Kurier</label>
              <select
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                required
                data-testid="slot-user"
                className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
              >
                <option value="">— wybierz —</option>
                {couriers.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.name || c.username} · {c.role}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="font-label text-[10px] block mb-1">Data</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
              data-testid="slot-date"
              className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
              data-testid="slot-all-day"
            />
            <span>Cały dzień (bez konkretnych godzin)</span>
          </label>
          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-label text-[10px] block mb-1">Godzina od</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="slot-start"
                  className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
                />
              </div>
              <div>
                <label className="font-label text-[10px] block mb-1">Godzina do</label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="slot-end"
                  className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
                />
              </div>
            </div>
          )}
          <div>
            <label className="font-label text-[10px] block mb-1">Notatka (opcjonalnie)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              data-testid="slot-notes"
              placeholder="np. wolałbym rano, mogę zostać do 18:00..."
              className="w-full border-2 border-zinc-900 px-3 py-2 text-sm focus:outline-none focus:border-brand"
            />
          </div>
          {canManage && (
            <div>
              <label className="font-label text-[10px] block mb-1">Uwagi koordynatora (widoczne dla kuriera)</label>
              <textarea
                value={form.review_notes}
                onChange={(e) => setForm({ ...form, review_notes: e.target.value })}
                rows={2}
                data-testid="slot-review-notes"
                className="w-full border-2 border-zinc-900 px-3 py-2 text-sm focus:outline-none focus:border-brand"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t-2 border-zinc-900 flex gap-3">
          <button
            type="submit"
            disabled={busy}
            data-testid="slot-save"
            className="flex-1 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover disabled:opacity-50"
          >
            {busy ? "Zapisuję..." : isEdit ? "Zapisz zmiany" : canManage ? "Utwórz slot" : "Wyślij zgłoszenie"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 border-2 border-zinc-900 font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white"
          >
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
